import {Btn, H5} from "../../../../../AbstractElements";
import {Card, CardBody, CardHeader, Col, Container, Modal, ModalBody, ModalFooter, ModalHeader, Row} from "reactstrap";
import React, {Fragment, useContext, useEffect, useState} from "react";
import IconBtn from "../../../../../CommonElements/IconButton";
import axiosConfig from "../../../../../Config/axios";
import Loader from "../../../../../Layout/Loader";
import ConfigurationForm from "../../Configuration/Component/Form";
import DataTable from "react-data-table-component";
import ButtonIcon from "../../../../../CommonElements/ButtonIcon";
import AuthorizationContext from "../../../../../_helper/Authorization";
import type {Setting, Configuration, SettingShowProps} from "../../Types/SettingTypes";
import type {AuthorizationContextType} from "../../../../../_helper/Authorization/AuthorizationTypes";

const SettingShow: React.FC<SettingShowProps> = ({setting}) => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const [configurations, setConfigurations] = useState<Configuration[]>([]);
    const [selectedConfiguration, setSelectedConfiguration] = useState<Configuration | undefined>();
    const [configurationModalVisibility, setConfigurationModalVisibility] = useState<boolean>(false);
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler} = authContext || {};

    const configurationModalToggle = () => {
        setConfigurationModalVisibility(!configurationModalVisibility);
    }

    const getConfigurations = () => {
        setLoaderVisibility(true);
        const config = {params: {setting_id: setting.id}};
        axiosConfig
            .get(`/system-core/configuration`, config)
            .then((response: { data: { data: Configuration[]; }; }) => {
                if (response?.data?.data) {
                    setConfigurations(response.data.data);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    const addConfiguration = (configuration: Configuration) => {
        const configurationList = [...configurations];
        const existingConfigurationIndex = configurationList.findIndex(existingConfiguration => {
            return existingConfiguration.id === configuration.id;
        });
        if (existingConfigurationIndex !== -1) {
            configurationList[existingConfigurationIndex] = configuration;
        } else {
            configurationList.push(configuration);
        }
        setConfigurations(configurationList);
        configurationModalToggle();
    }

    const handleConfigurationEdit = (configuration: Configuration) => {
        setSelectedConfiguration(configuration);
        configurationModalToggle();
    }

    const deleteConfiguration = (id: number) => {
        setLoaderVisibility(true);
        axiosConfig
            .delete(`/system-core/configuration/${id}`, {})
            .then((response: { data: { data: { id: number; }; }; }) => {
                if (response?.data?.data) {
                    setConfigurations(current => current.filter(config => {
                        return config.id !== response?.data?.data?.id;
                    }));
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    useEffect(() => {
        getConfigurations();
    }, [setting.id])


    const configurationTableColumns = [
        {
            name: 'name', selector: (row: Configuration) => row?.name, sortable: false,
        },
        {
            name: 'Key', selector: (row: Configuration) => row?.key, sortable: true,
        },
        {
            name: 'description', selector: (row: Configuration) => row?.description, sortable: true,
        },
        {
            name: 'Option', selector: (row: Configuration) => (<div className='d-flex align-items-right gap-2'>
                {
                    row?.system_defined ? null : <Fragment>
                        <IconBtn attrBtn={{onClick: () => handleConfigurationEdit(row)}} type={'edit'} permission={'update-configuration'}/>
                        <IconBtn confirm={true} onclick={() => deleteConfiguration(row?.id)} type={'delete'} permission={'delete-configuration'}/>
                    </Fragment>
                }
            </div>), sortable: false, right: true,
        }];

    return (<Fragment>
        <Loader visibility={loaderVisibility}/>

        <Container fluid={true}>
            <div className="email-wrap bookmark-wrap">
                <Row>
                    <Col>
                        <Card>
                            <CardBody>
                                <CardHeader className='d-flex align-items-center justify-content-between'>
                                    <H5>{setting.name}</H5>
                                    <div>
                                        <div className='d-flex align-items-center gap-2'>
                                            <ButtonIcon attrBtn={{
                                                onClick: () => setConfigurationModalVisibility(true)
                                            }} type={'add'} permission={'create-configuration'}/>
                                        </div>
                                    </div>
                                </CardHeader>
                                <Row>
                                    <Col md='3' sm='6' xsm='12' className='box-col-3 pe-0 file-spacing'>
                                        <div className='md-sidebar job-sidebar'>
                                            <div className={`md-sidebar-aside job-left-aside custom-scrollbar`}>
                                                <div className='file-sidebar'>
                                                    <Card>
                                                        <CardBody>
                                                            <h5 className="board-title">Key</h5>
                                                            <p className="board-text">{setting.key}</p>
                                                            <h5 className="board-title">Name</h5>
                                                            <p className="board-text">{setting.name}</p>
                                                            <h5 className="board-title">Description</h5>
                                                            <p className="board-text">{setting.description}</p>
                                                        </CardBody>
                                                    </Card>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md='9' sm='6' xsm='12'>
                                        <Card>
                                            <CardBody>
                                                <DataTable pagination paginationServer
                                                           columns={configurationTableColumns}
                                                           data={configurations}
                                                           highlightOnHover={true}
                                                           striped={true} responsive={true}/>

                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
            <Modal isOpen={configurationModalVisibility} toggle={configurationModalToggle} centered>
                <ModalHeader>
                    {selectedConfiguration ? 'Update' : 'Create'} Configuration
                </ModalHeader>
                <ModalBody>
                    <ConfigurationForm setting={setting} callBack={addConfiguration}
                                       configuration={selectedConfiguration}/>
                </ModalBody>
                <ModalFooter>
                    <Btn attrBtn={{color: 'primary', onClick: configurationModalToggle}}>Close</Btn>
                </ModalFooter>
            </Modal>
        </Container>
    </Fragment>)
}

export default SettingShow;
